import { ChevronRightIcon } from "@chakra-ui/icons";
import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Card,
  CardBody,
  Flex,
  Grid,
  Input,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import { useSupplies } from "../hooks/supplies.hooks";
import PrimaryButton from "../components/PrimaryButton";
import { Supply } from "../types/supplies.types";
import { ChangeEvent, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { createSupplyOrder } from "../mutations/supplies.mutations";
import { useNavigate } from "react-router-dom";
import { useSimpleToast } from "../hooks/toast.hooks";
import { useCenter } from "../hooks/centers.hooks";
import { Center } from "../types/center.types";
import CenterSelect from "../components/inputs/CenterSelect";
import CardTitle from "../components/CardTitle";
import FilePreview from "../components/FilePreview";
import SimpleSuccessFeedbackModal from "../components/modals/SimpleSuccessFeedbackModal";
import { Command } from "../types/command.types";
import { usePrintPageCommandPurchaseOrderFile } from "../hooks/printers.hooks";

function OrdersSuppliesPageFooter({
  total,
  handleClick,
}: {
  total: number;
  handleClick: () => void;
}) {
  return (
    <Flex
      h="100px"
      w="calc(100vw - 290px)"
      zIndex={1000}
      position={"fixed"}
      bottom={0}
      right={0}
      bg={"white"}
      justifyContent={"end"}
      p={3}
    >
      <Flex alignItems={"center"} gap={4}>
        <Flex direction={"column"}>
          <Text>Total</Text>
          <Text
            fontWeight={600}
            fontSize="24px"
            lineHeight={"36px"}
            color={"gray.950"}
          >
            {(total / 100).toFixed(2).toString().replace(".", ",")} €
          </Text>
        </Flex>
        <PrimaryButton
          w="fit-content"
          onClick={() => handleClick()}
          isDisabled={total === 0 || Number.isNaN(total)}
        >
          Passer la commande
        </PrimaryButton>
      </Flex>
    </Flex>
  );
}

type OrdersSuppliesPageSelectSuppliesProps = {
  supplyOrder: SupplyOrder[];
  setSupplyOrder: (order: SupplyOrder[]) => void;
  onValidate: () => void;
};

function OrdersSuppliesPageSelectSupplies({
  supplyOrder,
  setSupplyOrder,
  onValidate,
}: OrdersSuppliesPageSelectSuppliesProps) {
  const supplies = useSupplies(
    1,
    1000,
    undefined,
    { status: "ACTIVE" },
    undefined
  );

  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    supply: Supply
  ) => {
    const exist = supplyOrder.find((so) => so.supply.id === supply.id);
    const quantity = Number.isNaN(Number.parseInt(event.target.value))
      ? 0
      : Number.parseInt(event.target.value);
    if (exist) {
      exist.quantity = quantity;
      setSupplyOrder([...supplyOrder]);
    } else {
      setSupplyOrder([
        ...supplyOrder,
        {
          supply,
          quantity: quantity,
        },
      ]);
    }
  };

  const getSupplyOrderPrice = (supply: Supply) => {
    const found = supplyOrder.find((so) => so.supply.id === supply.id);
    if (found) {
      return found.quantity * supply.price;
    }
    return 0;
  };

  const getSupplyOrderTotalPrice = () => {
    return supplyOrder
      .map((so) => so.quantity * so.supply.price)
      .reduce((p, v) => p + v, 0);
  };

  return (
    <>
      {supplies == null && <>---</>}
      {supplies != null && (
        <Box pb={"100px"}>
          <VStack
            alignItems={"start"}
            bg="zinc.50"
            rounded="xl"
            px="4px"
            w="100%"
          >
            {/* HEADER */}
            <Flex w="100%" lineHeight={"44px"} px="1rem" py="4px">
              <Grid
                gridTemplateColumns={"1fr 220px 130px"}
                w="100%"
                pr="10px"
                color="gray.500"
              >
                <Box>Produit</Box>
                <Box>Quantité</Box>
                <Box>Total</Box>
              </Grid>
            </Flex>

            {/* BODY */}
            <Flex w="100%" direction={"column"} gap="2px" mb="4px">
              {supplies?.data &&
                supplies.data.sort((a, b) => a.index - b.index).map((supply) => {
                  return (
                    <Grid
                      key={supply.id}
                      lineHeight={"72px"}
                      bg="white"
                      p="1rem"
                      gridTemplateColumns={"1fr 190px 230px"}
                      _first={{ roundedTop: "xl" }}
                      _last={{ roundedBottom: "xl" }}
                    >
                      <Flex>
                        <FilePreview
                          id={supply.fileId ?? undefined}
                          mr={4}
                          w="64px"
                          h="64px"
                        ></FilePreview>
                        <Flex direction={"column"}>
                          <Box
                            color={"gray.900"}
                            fontSize={"14px"}
                            fontWeight={500}
                            lineHeight={"21px"}
                          >
                            {supply.label}
                          </Box>
                          <Box
                            color={"gray.500"}
                            fontSize={"14px"}
                            fontWeight={400}
                            lineHeight={"21px"}
                          >
                            {supply.description}
                          </Box>
                          <Box
                            color={"gray.900"}
                            fontSize={"14px"}
                            fontWeight={500}
                            lineHeight={"21px"}
                          >
                            {(supply.price / 100)
                              .toFixed(2)
                              .toString()
                              .replace(".", ",")}{" "}
                            €
                          </Box>
                        </Flex>
                      </Flex>
                      <Box>
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          defaultValue={0}
                          name={""}
                          onChange={(event) => handleChange(event, supply)}
                        ></Input>
                      </Box>
                      <Flex justifyContent={"center"}>
                        {(getSupplyOrderPrice(supply) / 100)
                          .toFixed(2)
                          .toString()
                          .replace(".", ",")}
                        &nbsp;€
                      </Flex>
                    </Grid>
                  );
                })}
            </Flex>
          </VStack>
        </Box>
      )}

      <OrdersSuppliesPageFooter
        total={getSupplyOrderTotalPrice()}
        handleClick={onValidate}
      ></OrdersSuppliesPageFooter>
    </>
  );
}

type OrdersSuppliesPageSelectCenterProps = {
  center?: Center;
  setCenter: (center?: Center) => void;
  onAbort?: () => void;
  onValidate?: () => void;
};

function OrdersSuppliesPageSelectCenter({
  center,
  setCenter,
  onAbort,
  onValidate,
}: OrdersSuppliesPageSelectCenterProps) {
  return (
    <Flex justifyContent="center">
      <Flex direction="column" alignItems="center" w="50%" gap={4}>
        <CenterSelect centerSelected={center} setCenterSelected={setCenter} />

        <Flex gap={4}>
          <Button variant="outline" onClick={onAbort}>
            Retour
          </Button>
          <Button
            bg="brand.600"
            color="white"
            variant="solid"
            onClick={onValidate}
            isDisabled={center === undefined}
          >
            Continuer
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}

type OrdersSuppliesPageValidationTypes = {
  center?: Center;
  supplyOrder: SupplyOrder[];
  onAbort?: () => void;
  onValidate?: () => void;
  disabled?: boolean;
};

function OrdersSuppliesPageValidation({
  center: outerCenter,
  supplyOrder,
  onAbort,
  onValidate,
  disabled = false,
}: OrdersSuppliesPageValidationTypes) {
  const { data: center } = useCenter(outerCenter?.id);

  const total = useMemo(() => {
    if (Array.isArray(supplyOrder)) {
      return supplyOrder.reduce(
        (total, so) => total + so.supply.price * so.quantity,
        0
      );
    }
    return undefined;
  }, [supplyOrder]);
  return (
    <Flex justifyContent={"center"} w="100%">
      <Flex direction="column" alignItems={"center"} w="100%" gap={12}>
        {/* TWO MAIN SECTIONS */}
        <Flex gap={12} w="100%">
          <Flex flexGrow={2} w="100%">
            <Card w="100%">
              <CardTitle backgroundColor="gray.50">
                <Text
                  lineHeight="27px"
                  fontSize="18px"
                  fontWeight="600"
                  color="black"
                >
                  Détails de la commande
                </Text>
              </CardTitle>
              <CardBody>
                <Flex direction="column">
                  {Array.isArray(supplyOrder) &&
                    supplyOrder.filter((so) => so.quantity > 0).map((so) => (
                      <Flex>
                        <Box
                          w="100%"
                          mb={4}
                          borderBottom="solid 1px"
                          borderColor="gray.100"
                          pb={2}
                        >
                          <Flex
                            h="100%"
                            justifyContent={"space-between"}
                            alignItems={"center"}
                          >
                            <Flex>
                              {so.supply.fileId && (
                                <FilePreview
                                  id={so.supply.fileId}
                                  mr={4}
                                  w="64px"
                                  h="64px"
                                ></FilePreview>
                              )}
                              <Flex direction="column">
                                <Text
                                  lineHeight="21px"
                                  fontSize="14px"
                                  fontWeight="500"
                                  color="gray.900"
                                >
                                  {so.supply.label}
                                </Text>
                                <Text
                                  lineHeight="21px"
                                  fontSize="14px"
                                  fontWeight="400"
                                  color="gray.500"
                                >
                                  {so.supply.label}
                                </Text>
                              </Flex>
                            </Flex>
                            <Flex
                              justifyContent="flex-end"
                              alignItems="baseline"
                              gap={4}
                            >
                              <Text
                                lineHeight="21px"
                                fontSize="14px"
                                fontWeight="500"
                                color="gray.500"
                              >
                                {(so.supply.price / 100)
                                  .toString()
                                  .replace(".", ",")}{" "}
                                € x {so.quantity}
                              </Text>
                              <Text
                                lineHeight="18px"
                                fontSize="12px"
                                fontWeight="500"
                                color="gray.900"
                              >
                                {((so.supply.price / 100) * so.quantity)
                                  .toString()
                                  .replace(".", ",")}{" "}
                                €
                              </Text>
                            </Flex>
                          </Flex>
                        </Box>
                      </Flex>
                    ))}

                  <Flex justifyContent="end" alignItems="baseline" gap={4}>
                    <Text
                      lineHeight="18px"
                      fontSize="12px"
                      fontWeight="500"
                      color="gray.500"
                    >
                      Total
                    </Text>
                    <Text
                      lineHeight="36px"
                      fontSize="24px"
                      fontWeight="600"
                      color="gray.900"
                    >
                      {total === undefined
                        ? "---"
                        : (total / 100).toString().replace(".", ",")}
                      €
                    </Text>
                  </Flex>
                </Flex>
              </CardBody>
            </Card>
          </Flex>
          <Flex flexGrow={1} w="100%">
            <Card w="100%">
              <CardTitle>
                <Text
                  lineHeight="27px"
                  fontSize="18px"
                  fontWeight="600"
                  color="black"
                >
                  {center?.name}
                </Text>
              </CardTitle>
              <CardBody>
                <Box>
                  <Text
                    lineHeight="27px"
                    fontSize="18px"
                    fontWeight="600"
                    color="gray.700"
                  >
                    Adresse de facturation
                  </Text>
                  {center && (
                    <>
                      <Text>{center.enterprise?.name}</Text>
                      <Text>
                        {center.enterprise?.address?.streetNumber}{" "}
                        {center.enterprise?.address?.streetName},
                      </Text>
                      <Text>
                        {center.enterprise?.address?.postalCode}{" "}
                        {center.enterprise?.address?.city}
                      </Text>
                    </>
                  )}
                </Box>
                <Box mt={4}>
                  <Text
                    lineHeight="27px"
                    fontSize="18px"
                    fontWeight="600"
                    color="gray.700"
                  >
                    Adresse de livraison
                  </Text>
                  {center && (
                    <>
                      <Text>{center.name}</Text>
                      <Text>
                        {center.shippingAddress?.streetNumber}{" "}
                        {center.shippingAddress?.streetName},
                      </Text>
                      <Text>
                        {center.shippingAddress?.postalCode}{" "}
                        {center.shippingAddress?.city}
                      </Text>
                    </>
                  )}
                </Box>
              </CardBody>
            </Card>
          </Flex>
        </Flex>
        {/* ACTION BUTTONS */}
        <Flex gap={4}>
          <Button variant="outline" onClick={onAbort}>
            Retour
          </Button>
          <Button
            bg="brand.600"
            color="white"
            variant="solid"
            onClick={onValidate}
            isDisabled={center === undefined || disabled}
          >
            Valider la commande
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}

interface SupplyOrder {
  supply: Supply;
  quantity: number;
}

function OrdersSuppliesPage() {
  const [commandId, setCommandId] = useState<Command['id'] | null>(null);
  const [step, setStep] = useState(0);
  const [supplyOrder, setSupplyOrder] = useState<SupplyOrder[]>([]);
  const [center, setCenter] = useState<Center | undefined>(undefined);
  const successModal = useDisclosure();
  const { mutate: mutateCreateSupplyOrder } = useMutation(createSupplyOrder, {
    onSuccess: async (data) => {
      if (data.id) {
        setCommandId(data.id);
      }
      successModal.onOpen();
    },
  });
  const isValidatingRef = useRef(false)
  const toast = useSimpleToast();
  const handleSubmit = () => {
    if (isValidatingRef.current === false) {
      isValidatingRef.current = true
      if (center?.id === undefined) {
        toast.error("Erreur", "La commande n'a pas pu être passé");
        return;
      }
      mutateCreateSupplyOrder({
        centerId: center?.id,
        order: supplyOrder.map((so) => ({
          supplyId: so.supply.id,
          quantity: so.quantity,
        })),
      });
    }
  };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { printPageCommandPurchaseOrder } =
    usePrintPageCommandPurchaseOrderFile({ id: commandId });

  return (
    <>
      <Breadcrumb
        spacing="8px"
        separator={<ChevronRightIcon color="gray.500" />}
      >
        <BreadcrumbItem>
          <BreadcrumbLink
            href="/orders"
            color="gray.600"
            fontWeight={600}
            fontSize="14px"
          >
            Commandes
          </BreadcrumbLink>
        </BreadcrumbItem>

        <BreadcrumbItem>
          <BreadcrumbLink
            href="#"
            color="brand.700"
            fontWeight={600}
            fontSize="14px"
          >
            Acheter des fournitures
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <br />
      <PageTitle>Acheter des fournitures</PageTitle>
      <br />
      <br />

      {step === 0 && (
        <OrdersSuppliesPageSelectSupplies
          supplyOrder={supplyOrder}
          setSupplyOrder={setSupplyOrder}
          onValidate={() => setStep(1)}
        />
      )}
      {step === 1 && (
        <OrdersSuppliesPageSelectCenter
          center={center}
          setCenter={setCenter}
          onAbort={() => setStep(0)}
          onValidate={() => setStep(2)}
        />
      )}
      {step === 2 && (
        <OrdersSuppliesPageValidation
          center={center}
          supplyOrder={supplyOrder}
          onAbort={() => setStep(1)}
          onValidate={handleSubmit}
          disabled={isValidatingRef.current === true}
        />
      )}

      <SimpleSuccessFeedbackModal
        isOpen={successModal.isOpen}
        onContinue={async () => {
          await queryClient.invalidateQueries();
          navigate("/orders");
        }}
        onPrint={() => {
          printPageCommandPurchaseOrder();
        }}
      />
    </>
  );
}

export default OrdersSuppliesPage;
